import React from "react";
import ReactExport from "react-data-export";

import styled from "@emotion/styled";

import { Button } from "@/components/Buttons";

import { formatDate } from "@/lib/date";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const TitleButton = styled(Button)`
  margin-left: 5px;
`;

const DEFAULT_EXCEL_SCHEME = {
  type: "유형",
  customerName: "수하인명",
  customerMobile: "수하인연락처",
  customerMobile2: "",
  customerAddress: "수하인주소",
  customerAddressDetail: "수하인상세주소",
  customerAddressPostalCode: "우편번호",
  prepaid: "선불",
  productName: "제품명",
  productCategory: "제품종류",
  productPrice: "제품가격",
  productCount: "수량",
  memoFromCustomer: "고객전달사항",
  frontdoorPassword: "공동현관비밀번호",
  orderIdFromCorp: "기업고유번호",
  etc1: "기타사항1",
  etc2: "기타사항2",
  etc3: "기타사항3",
};

const normMobile = (v) => String(v ?? "").replace(/[\s\u00a0-]/g, "");
const normText = (v) =>
  String(v ?? "")
    .replace(/[\s\u00a0]/g, "")
    .trim();

const makeGroupKey = (name, mobile, address) =>
  [normText(name), normMobile(mobile), normText(address)].join("__");

const detectOriginalDataKeys = (originalData, data, scheme) => {
  if (!originalData?.length || !data?.length) {
    return {
      nameKey: null,
      mobileKey: null,
      mobileKey2: null,
      addressKey: null,
      productNameKey: null,
      orderIdKey: null,
    };
  }

  const availableHeaders = Object.keys(originalData[0] ?? {});
  const resolveByScheme = (v) => (v && availableHeaders.includes(v) ? v : null);

  // 1단계: scheme에 정의된 헤더가 원본에 "존재"하면 그대로 확정
  const schemeKeys = {
    nameKey: resolveByScheme(scheme?.customerName),
    mobileKey: resolveByScheme(scheme?.customerMobile),
    mobileKey2: resolveByScheme(scheme?.customerMobile2),
    addressKey: resolveByScheme(scheme?.customerAddress),
    productNameKey: resolveByScheme(scheme?.productName),
    orderIdKey: resolveByScheme(scheme?.orderIdFromCorp),
  };

  let {
    nameKey,
    mobileKey,
    mobileKey2,
    addressKey,
    productNameKey,
    orderIdKey,
  } = schemeKeys;

  // 2단계: scheme로 확정 못 한 키만 fallback으로 채움
  const needsFallback =
    !nameKey || !mobileKey || !addressKey || !productNameKey || !orderIdKey;

  if (needsFallback) {
    for (const sampleData of data) {
      for (const sampleRow of originalData) {
        for (const [k, v] of Object.entries(sampleRow)) {
          if (
            k === schemeKeys.nameKey ||
            k === schemeKeys.mobileKey ||
            k === schemeKeys.mobileKey2 ||
            k === schemeKeys.addressKey ||
            k === schemeKeys.productNameKey ||
            k === schemeKeys.orderIdKey
          ) {
            continue;
          }

          const strVal = normText(v);
          if (!strVal) continue;

          if (!nameKey && strVal === normText(sampleData.receiverName)) {
            nameKey = k;
          }
          if (
            normMobile(v) &&
            normMobile(v) === normMobile(sampleData.receiverMobile)
          ) {
            if (!mobileKey) mobileKey = k;
            else if (!mobileKey2 && k !== mobileKey) mobileKey2 = k;
          }
          if (!addressKey && strVal === normText(sampleData.unrefinedAddress)) {
            addressKey = k;
          }
          if (
            !productNameKey &&
            strVal &&
            normText(sampleData.productName).includes(strVal)
          ) {
            productNameKey = k;
          }
          if (
            !orderIdKey &&
            sampleData.orderIdFromCorp != null &&
            strVal === normText(sampleData.orderIdFromCorp)
          ) {
            orderIdKey = k;
          }
        }
        if (nameKey && mobileKey && addressKey && productNameKey && orderIdKey)
          break;
      }
      if (nameKey && mobileKey && addressKey && productNameKey && orderIdKey)
        break;
    }
  }

  return {
    nameKey,
    mobileKey,
    mobileKey2,
    addressKey,
    productNameKey,
    orderIdKey,
  };
};

const buildGroupCandidatesMap = (data) => {
  const map = new Map();
  const addEntry = (key, candidate) => {
    if (!map.has(key)) map.set(key, []);
    const list = map.get(key);
    if (!list.some((c) => c.index === candidate.index)) {
      list.push(candidate);
    }
  };
  data.forEach((d, index) => {
    const candidate = {
      index,
      productName: normText(d.productName),
      used: false,
    };
    addEntry(
      makeGroupKey(d.receiverName, d.receiverMobile, d.unrefinedAddress),
      candidate,
    );
    if (d.receiverMobile2 && d.receiverMobile2 !== d.receiverMobile) {
      addEntry(
        makeGroupKey(d.receiverName, d.receiverMobile2, d.unrefinedAddress),
        candidate,
      );
    }
  });
  return map;
};

const pickBestCandidate = (candidates, originalProductName) => {
  if (!candidates?.length) return null;

  const target = normText(originalProductName);

  const matched = candidates.filter(
    (c) => !c.used && (!target || c.productName.includes(target)),
  );
  if (!matched.length) {
    const remaining = candidates.filter((c) => !c.used);
    return remaining.length === 1 ? remaining[0] : null;
  }

  matched.sort((a, b) => {
    const aExact = a.productName === target ? 1 : 0;
    const bExact = b.productName === target ? 1 : 0;
    if (aExact !== bExact) return bExact - aExact;
    return b.productName.length - a.productName.length;
  });

  return matched[0];
};

const buildIndexToOrderIdMap = (originalData, data, scheme) => {
  const {
    nameKey,
    mobileKey,
    mobileKey2,
    addressKey,
    productNameKey,
    orderIdKey,
  } = detectOriginalDataKeys(originalData, data, scheme);

  const map = new Map();
  if (!orderIdKey || !originalData?.length) return map;

  const groupMap = buildGroupCandidatesMap(data);

  originalData.forEach((row) => {
    const mobile1 = row[mobileKey]?.toString().trim();
    const mobile2 = row[mobileKey2]?.toString().trim();
    const rawMobile = mobile1 || mobile2 || "";

    const groupKey = makeGroupKey(row[nameKey], rawMobile, row[addressKey]);
    const candidates = groupMap.get(groupKey);
    if (!candidates) return;

    const originalProductName = productNameKey ? row[productNameKey] : "";
    const best = pickBestCandidate(candidates, originalProductName);
    if (best) {
      best.used = true; // 소진 처리 → 같은 행이 중복 매칭되지 않게 함
      map.set(best.index, String(row[orderIdKey] ?? ""));
    }
  });

  return map;
};

export default function ExcelAfterBulkRegister({
  data,
  originalData,
  excelScheme,
  spot,
}) {
  const scheme = excelScheme ?? {
    ...DEFAULT_EXCEL_SCHEME,
    ...spot?.excelSchemeParsed,
  };

  const indexToOrderIdMap = buildIndexToOrderIdMap(originalData, data, scheme);

  // data 기반으로 출력하되, 사측주문번호만 원본값으로 교체 (인덱스로 조회)
  const mergedData = data.map((d, index) => {
    const originalOrderId = indexToOrderIdMap.get(index);
    return {
      ...d,
      orderIdFromCorp:
        originalOrderId != null && originalOrderId !== ""
          ? originalOrderId
          : d.orderIdFromCorp,
    };
  });

  return (
    <ExcelFile
      filename={`체인로지스_두발히어로_주문내역_${formatDate(
        new Date(),
        "MMdd",
      )}`}
      element={
        <TitleButton type="button" variant="outline-secondary" width="120px">
          접수 다운로드
        </TitleButton>
      }
    >
      <ExcelSheet data={mergedData} name="Employees">
        <ExcelColumn label="예약번호" value="bookId" />
        <ExcelColumn label="사측주문번호" value="orderIdFromCorp" />
        <ExcelColumn label="송하인명" value="senderName" />
        <ExcelColumn label="송하인주소" value="senderAddress" />
        <ExcelColumn label="송하인상세주소" value="senderAddressDetail" />
        <ExcelColumn label="송하인연락처" value="senderMobile" />
        <ExcelColumn label="수하인명" value="receiverName" />
        <ExcelColumn label="수하인주소" value="receiverAddress" />
        <ExcelColumn label="수하인상세주소" value="receiverAddressDetail" />
        <ExcelColumn label="수하인연락처1" value="receiverMobile" />
        <ExcelColumn label="수하인연락처2" value="receiverMobile2" />
        <ExcelColumn label="고객요청사항" value="memoFromCustomer" />
        {/* <ExcelColumn label="결제방법" value="orderIdFromCorp" /> */}
        <ExcelColumn label="물품가격" value="productPrice" />
        <ExcelColumn label="물품명" value="productName" />
        <ExcelColumn label="기타사항1" value="etc1" />
        <ExcelColumn label="기타사항2" value="etc2" />
        <ExcelColumn label="기타사항3" value="etc3" />
        <ExcelColumn label="지역그룹" value="receiverAllocationGroupName" />
        {/* <ExcelColumn label="Gender" value="sex" />
        <ExcelColumn
          label="Marital Status"
          value={(col) => (col.is_married ? "Married" : "Single")}
        /> */}
      </ExcelSheet>
    </ExcelFile>
  );
}
